/**
 * Shared service-menu sidebar (owner ask 2026-07-22 #6), used by BOTH `riders-list-page.ts` and
 * `profile-page.ts` (previously only the riders list had it, which was one more way the two pages
 * looked like they came from different sites). Anchors where the product's real navigation will
 * eventually live, without building any of it yet — lists v1's own "dream menu" in its own order
 * (Home / Soundman / My Riders / Inbox / Outbox / Event manager / Warehouse / My
 * Profile).
 *
 * There is NO role chip here (owner 19.10.2026: «вот эту жёлтую плашку слева убирай нахрен. Роль
 * выбирается в настройках профиля и всё»): the role is set on My profile only.
 *
 * Only "Home" (-> the public landing page), "My Riders", "Offers", "Warehouse" and "My Profile" are wired. Every other
 * item is a genuinely disabled `<button>` with a "— coming soon" tooltip: this project's hard rule
 * is an honest disabled placeholder, never a button that LOOKS clickable and silently does nothing
 * (see `editor-page.ts`'s own `#btn-addobject` for the established pattern this reuses).
 */
import { navigate } from "../app/router";
import { getStoredUser } from "../api/http";
import { icon } from "./icons";
import { getTheme, setTheme, type ThemeName } from "./theme";
import { countUnreadItems, unreadBadgeText } from "../shows/inbox";
import { loadInboxItems } from "./shows-inbox";
import { getLocale, listLocales, setLocale, t, type Locale } from "../i18n";
import { whenIdleAfter } from "../app/when-idle";

export type SideNavCurrent = "riders" | "offers" | "shows" | "warehouse" | "profile" | "admin";

/** Markup only — `current` decides which of the wired items renders `.active`/`aria-current`. */
export function renderSideNavHtml(current: SideNavCurrent): string {
  const isRiders = current === "riders";
  const isOffers = current === "offers";
  const isShows = current === "shows";
  const isWarehouse = current === "warehouse";
  const isProfile = current === "profile";
  const isAdmin = current === "admin";
  // Shown only to administrators, and only as a convenience: `AdminGuard` re-reads `is_admin` from
  // the database on every admin call, so a stale stored flag can at most reveal a link whose page
  // then says "not available". Before this, /admin was reachable only by typing the URL — the owner
  // had never opened it (report 2026-08-08).
  const showAdmin = getStoredUser()?.isAdmin === true;
  return `
  <nav id="side-nav" aria-label="${t("sideNav.mainAriaLabel")}">
    <div id="side-nav-strip">
    <div id="side-nav-items">
      <button id="side-nav-home" type="button" class="side-nav-item" title="${t("sideNav.home")}">${icon("home")}<span>${t("sideNav.home")}</span></button>
      <button type="button" class="side-nav-item" disabled title="${t("sideNav.soundmanComingSoon")}">${icon("soundman")}<span>${t("sideNav.soundman")}</span></button>
      <button id="side-nav-riders" type="button" class="side-nav-item${isRiders ? " active" : ""}" ${isRiders ? 'aria-current="page"' : ""} title="${t("sideNav.myRiders")}">${icon("riders-list")}<span>${t("sideNav.myRiders")}</span></button>
      <button id="side-nav-offers" type="button" class="side-nav-item${isOffers ? " active" : ""}" ${isOffers ? 'aria-current="page"' : ""} title="${t("sideNav.offers")}">${icon("offers")}<span>${t("sideNav.offers")}</span></button>
      <button id="side-nav-shows" type="button" class="side-nav-item${isShows ? " active" : ""}" ${isShows ? 'aria-current="page"' : ""} title="${t("sideNav.shows")}">${icon("calendar")}<span>${t("sideNav.shows")}</span></button>
      <button id="side-nav-inbox" type="button" class="side-nav-item" title="${t("sideNav.inbox")}">${icon("inbox")}<span>${t("sideNav.inbox")}</span><b class="side-nav-badge" hidden></b></button>
      <button type="button" class="side-nav-item" disabled title="${t("sideNav.outboxComingSoon")}">${icon("outbox")}<span>${t("sideNav.outbox")}</span></button>
      <button type="button" class="side-nav-item" disabled title="${t("sideNav.eventManagerComingSoon")}">${icon("event-manager")}<span>${t("sideNav.eventManager")}</span></button>
      <button id="side-nav-warehouse" type="button" class="side-nav-item${isWarehouse ? " active" : ""}" ${isWarehouse ? 'aria-current="page"' : ""} title="${t("sideNav.warehouse")}">${icon("warehouse")}<span>${t("sideNav.warehouse")}</span></button>
      ${
        showAdmin
          ? `<button id="side-nav-admin" type="button" class="side-nav-item${isAdmin ? " active" : ""}" ${
              isAdmin ? 'aria-current="page"' : ""
            } title="${t("sideNav.admin")}">${icon("properties")}<span>${t("sideNav.admin")}</span></button>`
          : ""
      }
      <button id="side-nav-profile" type="button" class="side-nav-item${isProfile ? " active" : ""}" ${isProfile ? 'aria-current="page"' : ""} title="${t("sideNav.myProfile")}">${icon("properties")}<span>${t("sideNav.myProfile")}</span></button>
    </div>
    <div id="side-nav-theme">
      <label for="side-nav-theme-select" class="hint">${t("sideNav.designThemeLabel")}</label>
      <select id="side-nav-theme-select" aria-label="${t("sideNav.designThemeLabel")}">
        <option value="scb">SCB</option>
        <option value="protools">Pro Tools</option>
      </select>
    </div>
    <div id="side-nav-locale">
      <label for="side-nav-locale-select" class="hint">${t("sideNav.languageLabel")}</label>
      <select id="side-nav-locale-select" aria-label="${t("sideNav.languageLabel")}">
        ${listLocales()
          .map(([code, label]) => `<option value="${code}">${label}</option>`)
          .join("")}
      </select>
    </div>
    </div>
  </nav>`;
}

/** Кнопка меню, отвечающая за этот раздел. Вынесено из разметки, потому что подсветку теперь
 *  переставляют НА МЕСТЕ: меню строится один раз на весь кабинет и при переходе не пересобирается
 *  (`app/cabinet-shell.ts`). Пара «разметка — подсветка» иначе разошлась бы при первом же
 *  добавлении раздела. */
const SIDE_NAV_ITEM_ID: Record<SideNavCurrent, string> = {
  riders: "side-nav-riders",
  offers: "side-nav-offers",
  shows: "side-nav-shows",
  warehouse: "side-nav-warehouse",
  profile: "side-nav-profile",
  admin: "side-nav-admin",
};

/**
 * ПЕРЕСТАВИТЬ ПОДСВЕТКУ, НЕ ПЕРЕСОБИРАЯ МЕНЮ (владелец, 29.10.2026: «почему при переключени на
 * варехауз левая панель кабинета пропадает на короткое время?»).
 *
 * До 29.10.2026 каждая страница кабинета рисовала меню заново, поэтому при любом переходе узел
 * `#side-nav` удалялся и создавался другой — замер: `#riders-page` снят, через 14 мс добавлен
 * `#my-equipment-page`, и меню в нём уже ДРУГОЙ элемент. Отсюда и мигание панели.
 */
export function setSideNavCurrent(root: ParentNode, current: SideNavCurrent): void {
  const wanted = SIDE_NAV_ITEM_ID[current];
  for (const id of Object.values(SIDE_NAV_ITEM_ID)) {
    const button = root.querySelector<HTMLButtonElement>(`#${id}`);
    if (!button) continue; // пункта может не быть вовсе — «Администрация» видна не всем
    const active = id === wanted;
    button.classList.toggle("active", active);
    if (active) button.setAttribute("aria-current", "page");
    else button.removeAttribute("aria-current");
  }
}

/** Wires the three live items + the theme switcher. Call once, right after the nav's HTML lands in
 *  the DOM. The switcher (owner ask 2026-07-22 Task 4) is a plain `<select>`, not a pair of
 *  buttons, deliberately — chrome-layout.spec.ts's page-wide icon sweep requires every `<button>`
 *  to carry an icon, and a two-value theme choice reads perfectly well as a select with no icon
 *  needed at all. */
export function initSideNav(root: ParentNode): void {
  root.querySelector<HTMLButtonElement>("#side-nav-home")?.addEventListener("click", () => navigate({ name: "landing" }));
  root.querySelector<HTMLButtonElement>("#side-nav-riders")?.addEventListener("click", () => navigate({ name: "riders" }));
  root.querySelector<HTMLButtonElement>("#side-nav-offers")?.addEventListener("click", () => navigate({ name: "offers" }));
  root.querySelector<HTMLButtonElement>("#side-nav-shows")?.addEventListener("click", () => navigate({ name: "shows", tab: "list" }));
  root.querySelector<HTMLButtonElement>("#side-nav-inbox")?.addEventListener("click", () => navigate({ name: "shows", tab: "inbox" }));
  root.querySelector<HTMLButtonElement>("#side-nav-warehouse")?.addEventListener("click", () => navigate({ name: "warehouse" }));
  root.querySelector<HTMLButtonElement>("#side-nav-profile")?.addEventListener("click", () => navigate({ name: "profile" }));
  root.querySelector<HTMLButtonElement>("#side-nav-admin")?.addEventListener("click", () => navigate({ name: "admin" }));

  // ЗНАЧОК ВХОДЯЩИХ УЕЗЖАЕТ С КРИТИЧЕСКОГО ПУТИ (SCRUM-887; владелец: «значок входящих не на
  // критическом пути (отложить/кешировать)»). Он тянет ТРИ списка — шоу, входящие офферы, свои
  // офферы, — и на странице райдеров они уходили РАНЬШЕ самого списка райдеров (замер: 1511 мс
  // против 1541). Число у значка может появиться секундой позже; список, за которым человек
  // пришёл, — нет.
  whenIdleAfter(1000, () => void showUnreadOnNav(root));

  const themeSelect = root.querySelector<HTMLSelectElement>("#side-nav-theme-select");
  if (themeSelect) {
    themeSelect.value = getTheme();
    themeSelect.addEventListener("change", () => {
      setTheme(themeSelect.value as ThemeName);
    });
  }

  const localeSelect = root.querySelector<HTMLSelectElement>("#side-nav-locale-select");
  if (localeSelect) {
    localeSelect.value = getLocale();
    localeSelect.addEventListener("change", () => {
      setLocale(localeSelect.value as Locale);
    });
  }
}

/**
 * The «Inbox» badge (SCRUM-629; owner 06.10: «в шоу пока не транслируем цифру, только в инбокс»): the inbox rows
 * waiting for the person, read exactly as the inbox tab reads them. «Shows» carries no number. Only when signed
 * in; a failure leaves the badge empty — a number that is not known is better absent than wrong.
 */
async function showUnreadOnNav(root: ParentNode): Promise<void> {
  if (!getStoredUser()) return;
  try {
    const count = countUnreadItems(await loadInboxItems());
    const text = unreadBadgeText(count);
    const button = root.querySelector<HTMLButtonElement>("#side-nav-inbox");
    const badge = button?.querySelector<HTMLElement>(".side-nav-badge");
    if (!button || !badge) return;
    badge.textContent = text;
    badge.hidden = text === "";
    button.title = text === "" ? t("sideNav.inbox") : `${t("sideNav.inbox")} — ${t("sideNav.unreadCount", { n: count })}`;
  } catch {
    /* the nav works without the number */
  }
}
